import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ease = [0.22, 1, 0.36, 1];

const initialForm = { name: "", email: "", company: "", phone: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our team will be in touch shortly.");
      setDone(true);
      setForm(initialForm);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 bg-ivory"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-5"
        >
          <span className="eyebrow flex items-center gap-3">
            <span className="w-10 h-px bg-brass" />
            Explore Opportunities
          </span>
          <h2 className="h-display text-4xl md:text-6xl mt-6">
            Make a lasting
            <br />
            <span className="italic text-brass">impression</span>.
          </h2>
          <p className="mt-8 text-forest-600 text-base md:text-lg leading-relaxed font-light max-w-md">
            Tell us about your brand. We'll respond with curated placement
            options across the network — usually within one business day.
          </p>

          <div className="mt-12 space-y-5" data-testid="contact-info">
            <div className="flex items-start gap-4">
              <Mail size={18} strokeWidth={1.4} className="text-brass mt-1" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                  Email
                </div>
                <div className="text-forest-800 mt-1">varun@civitechdigital.com</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease, delay: 0.15 }}
          onSubmit={submit}
          noValidate
          className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-forest-800/10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                Name *
              </label>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                className="editorial-input"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                Email *
              </label>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="editorial-input"
                placeholder="you@brand.com"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                Company
              </label>
              <input
                data-testid="contact-input-company"
                value={form.company}
                onChange={update("company")}
                className="editorial-input"
                placeholder="Brand or agency"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                Phone
              </label>
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                className="editorial-input"
                placeholder="+91"
              />
            </div>
            <div className="md:col-span-2 mt-4">
              <label className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
                Message *
              </label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={4}
                className="editorial-input resize-none"
                placeholder="Tell us about your campaign objectives…"
              />
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-xs text-forest-500 max-w-sm leading-relaxed">
              By submitting this form you agree to be contacted by Green Signs
              about partnership opportunities.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold"
              data-testid="contact-submit-button"
            >
              {submitting ? "Sending…" : done ? "Sent — Send Another" : "Send Enquiry"}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
